import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useCartStore } from '@/stores/cartStore'
import './CartDrawer.css'

interface CartDrawerProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * CartDrawer — Bottom-sheet showing in-cart items grouped by shop.
 * Quantity controls + checkout button per shop.
 */
export function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const { t } = useTranslation('delivery')
  const items = useCartStore((s) => s.items)
  const updateQuantity = useCartStore((s) => s.updateQuantity)
  const removeItem = useCartStore((s) => s.removeItem)
  const navigate = useNavigate()

  // Group in-cart items by shop
  const grouped = useMemo(() => {
    const cartItems = items.filter((i) => i.inCart)
    const map: Record<string, typeof cartItems> = {}
    for (const item of cartItems) {
      if (!map[item.shopId]) map[item.shopId] = []
      map[item.shopId].push(item)
    }
    return map
  }, [items])

  const shopIds = Object.keys(grouped)
  const totalItems = items.filter((i) => i.inCart).length

  const computeShopTotal = useCallback((shopItems: typeof items) => {
    let total = 0
    let allPriced = true
    for (const item of shopItems) {
      if (item.price !== null) {
        total += item.price * item.quantity
      } else {
        allPriced = false
      }
    }
    return { total, allPriced }
  }, [])

  const handleCheckout = useCallback((shopId: string) => {
    onClose()
    navigate(`/checkout/${shopId}`)
  }, [onClose, navigate])

  return (
    <>
      <div
        className={`cart-drawer-backdrop ${isOpen ? 'open' : ''}`}
        onClick={onClose}
      />
      <div className={`cart-drawer ${isOpen ? 'open' : ''}`}>
        <div className="cart-drawer-handle" />
        <div className="cart-drawer-header">
          <h3>{t('cart.title', { count: totalItems })}</h3>
          <button className="cart-drawer-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="cart-drawer-body">
          {shopIds.length === 0 ? (
            <div className="cart-drawer-empty">
              {t('cart.emptyMessage')}
            </div>
          ) : (
            shopIds.map((shopId) => {
              const shopItems = grouped[shopId]
              const { total, allPriced } = computeShopTotal(shopItems)

              return (
                <div key={shopId} className="cart-shop-group">
                  <div className="cart-shop-name">{t('cart.shopLabel', { shopId: shopId.slice(0, 8) })}</div>

                  {shopItems.map((item, idx) => (
                    <div key={item.id} className="cart-item-row">
                      {item.thumbnailDataUrl ? (
                        <img src={item.thumbnailDataUrl} alt="" className="cart-item-thumb" />
                      ) : (
                        <div className="cart-item-thumb-placeholder">📦</div>
                      )}

                      <div className="cart-item-detail">
                        <div className="cart-item-name">{item.name || t('cart.itemFallbackName', { number: idx + 1 })}</div>
                        {item.price !== null ? (
                          <div className="cart-item-price">{t('cart.priceLabel', { price: item.price.toFixed(2) })}</div>
                        ) : (
                          <div className="cart-item-no-price">{t('cart.priceTBD')}</div>
                        )}
                      </div>

                      <div className="cart-qty-controls">
                        <button
                          className="cart-qty-btn"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                        >
                          −
                        </button>
                        <span className="cart-qty-val">{item.quantity}</span>
                        <button
                          className="cart-qty-btn"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        >
                          +
                        </button>
                      </div>

                      <button
                        className="cart-item-remove"
                        onClick={() => removeItem(item.id)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                        </svg>
                      </button>
                    </div>
                  ))}

                  <div className="cart-drawer-footer">
                    <div className="cart-total-row">
                      <span className="cart-total-label">{t('cart.subtotal')}</span>
                      <span className="cart-total-value">
                        {allPriced ? t('cart.priceLabel', { price: total.toFixed(2) }) : t('cart.somePricesTBD')}
                      </span>
                    </div>
                    <button
                      className="cart-checkout-btn"
                      onClick={() => handleCheckout(shopId)}
                    >
                      {t('cart.checkout')}
                    </button>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </>
  )
}
